import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { backButton, haptic } from '../lib/telegram';
import { Icon } from './Icon';

const TAB_ROOTS = ['/', '/groups', '/profile'];

/**
 * Wires Telegram's native Back button to in-app navigation on every
 * screen that is not a tab root.
 */
function useTelegramBackButton(): void {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (TAB_ROOTS.includes(location.pathname)) return;
    return backButton.show(() => {
      haptic.tap();
      // If the app was opened on a deep link there is no history to go back to.
      if (window.history.state?.idx > 0) void navigate(-1);
      else void navigate('/', { replace: true });
    });
  }, [location.pathname, navigate]);
}

/** Layout for the three main tabs: page content plus the bottom tab bar. */
const TABS = ['home', 'groups', 'profile'] as const;

export function TabLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  useTelegramBackButton();
  const active = Math.max(0, TABS.findIndex((tab) => (tab === 'home' ? pathname === '/' : pathname.startsWith(`/${tab}`))));
  return (
    <>
      <Outlet />
      <nav className="tabbar" aria-label="Main">
        <div className="tabbar-inner">
          {/* 60px tab + 6px gap per step */}
          <span className="tab-indicator" style={{ transform: `translateX(${active * 66}px)` }} aria-hidden />
          {TABS.map((tab) => (
            <NavLink
              key={tab}
              to={tab === 'home' ? '/' : `/${tab}`}
              end={tab === 'home'}
              className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
              aria-label={t(`nav.${tab}`)}
              title={t(`nav.${tab}`)}
              onClick={() => haptic.select()}
            >
              <Icon name={tab} size={22} />
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
}

/** Layout for detail screens: no tab bar; Telegram's Back button returns. */
export function StackLayout() {
  useTelegramBackButton();
  return <Outlet />;
}
